import {
  ArrowDownIcon,
  ArrowRightIcon,
  CalendarAltIcon,
  InfoIcon,
  LocationAltIcon,
  PhoneIcon,
  RmxEditIcon,
  UserIcon
} from 'cokitchen-icons';
import { ReactComponent as CutleryIcon } from 'assets/icons/cutlery.svg';
import { ReactComponent as QuestionIcon } from 'assets/icons/question-icon-filled.svg';
import useDashboardContext from 'contexts/dashboard.context';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import '../../styles.scss';
import {
  cleanObject,
  formatCurrency,
  formatDaysOfWeek,
  formatDeliveryTime,
  getItemsTotal,
  handleApiError,
  truncateString
} from 'utils/functions';
import MealItem from 'components/meal-item';
import ScheduledDeliveryModal from 'components/modals/scheduled-delivery-modal';
import DiscountCodeModal from 'components/modals/discount-code-modal';
import AppreciationModal from 'components/modals/appreciation-note-modal';
import AddNewNumberModal from 'components/modals/add-new-number';
import useOrdersContext from 'contexts/orders.context';
import BulkOrderService from 'services/bulk-order.service';
import AddressModal from 'components/modals/group-order/address-modal';
import { useNavigate } from 'react-router-dom';
import { QUICKEATS_BRAND_IDS, STORAGE_KEYS } from 'utils/constants';

import moment from 'moment';
import { apiService } from 'services/base.service';
import AssignMealModal from 'components/modals/assign-meal';
import { toast } from 'react-toastify';
import GroupOrderInfoModal from 'components/modals/group-order-info-modal';
import { OrderMealType } from 'models/order.model';
import { MemberListType } from 'models/member.model';
import { LocationObjectType } from 'models/location.model';
import QuickeatsBanner from 'components/quickeats-banner';

export type FormattedOrderType = {
  brand: { name: string; id: string };
  meals: OrderMealType[];
};

const GroupBasket = () => {
  const { setIsBackLink, setBackLinkTitle } = useDashboardContext();
  const { groupOrderMeals, setGroupOrderMeals, createGroupOrderObject, handleReset } =
    useOrdersContext();
  const [showScheduledDeliveryModal, setShowScheduledDeliveryModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showAppreciationNoteModal, setShowAppreciationNoteModal] = useState(false);
  const [showDiscountCodeModal, setShowDiscountCodeModal] = useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER);
  const [showMeals, setShowMeals] = useState(['']);
  const {
    calculatedOrderForm,
    setcalculatedOrderForm,
    calculatedOrder,
    createOrderForm,
    setCreateOrderForm
  } = useOrdersContext();
  const navigate = useNavigate();
  const [ignoreQuickeats, setIgnoreQuickeats] = useState(false);

  const showQuickeatsConfirm = useMemo(() => {
    const hasQuickeats = calculatedOrder?.meals?.some((meal) =>
      QUICKEATS_BRAND_IDS.includes(meal.brand.id)
    );
    const hasNotQuickeats = calculatedOrder?.meals?.some(
      (meal) => !QUICKEATS_BRAND_IDS.includes(meal.brand.id)
    );
    return hasQuickeats && hasNotQuickeats;
  }, [calculatedOrder]);

  const removeNonQuickeats = () => {
    const mealBrands = calculatedOrder.meals.filter((meal) =>
      QUICKEATS_BRAND_IDS.includes(meal.brand.id)
    );
    const meals = mealBrands
      .map((meals) => meals.meals)
      .flat()
      .map(({ id, quantity, addons }) => ({
        id: id,
        quantity,
        addons: addons?.map((addon) => ({ id: addon.id, quantity: addon.quantity }))
      }));
    apiService.post('order/group/basket/edit', {
      baseURL: process.env.REACT_APP_ORDER_API_URL,
      data: {
        calculated_order_id: calculatedOrder.id,
        meals,
        pickup: false
      }
    });
  };

  const firstLoad = useRef(true);

  useEffect(() => {
    setIsBackLink(true);
    setBackLinkTitle('Return to previous');
    return () => {
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  const confirm = () => {
    navigate('/orders/checkout?type=group');
  };

  useEffect(() => {
    setcalculatedOrderForm({
      ...calculatedOrderForm,
      address_details: createGroupOrderObject.address_details
    });
  }, []);

  const inviteMembers = (members: MemberListType[]) => {
    try {
      apiService.post('order/add-members-to-company', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: {
          calculated_order_id: calculatedOrder.id,
          company_user_ids: members.map((member) => member.user.id)
        }
      });

      toast(<ToastMessage type='success' message='Invite has been sent' />);
    } catch (err) {
      handleApiError(err, 'Failed to add member');
    }
  };

  const cancelOrder = async () => {
    try {
      const res = await apiService.post('order/group/cancel', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: { calculated_order_id: calculatedOrder?.id }
      });
      if (res?.data) {
        toast(<ToastMessage type='success' message='Order has been cancelled ' />);
        handleReset();
        navigate('/orders');
      }
    } catch (err) {
      handleApiError(err, 'Problem encountered while cancelling order');
    }
  };

  useEffect(() => {
    isConfirmDisabled();
  }, [calculatedOrderForm, groupOrderMeals]);

  const isConfirmDisabled = () => {
    setIsDisabled(false);
    groupOrderMeals?.map(({ quantity, members }) => {
      if (quantity < (members?.length || 0)) {
        setIsDisabled(true);
      }
    });
    if (
      !calculatedOrderForm.recurring &&
      calculatedOrderForm.scheduled_delivery_date === '' &&
      calculatedOrderForm.scheduled_delivery_time === ''
    ) {
      setIsDisabled(true);
    }
  };

  const updateDeliveryDetails = async () => {
    const {
      address_details,
      lat,
      lng,
      scheduled_delivery_date,
      scheduled_delivery_time,

      discount_code
    } = calculatedOrderForm;
    try {
      const payload = {
        calculated_order_id: calculatedOrder.id,
        address_details: address_details?.address_line === '' ? undefined : address_details,
        scheduled_delivery_date,
        scheduled_delivery_time,
        scheduled: true,
        lat: lat === '0' ? undefined : lat,
        lng: lng === '0' ? undefined : lng,

        discount_code
      };
      await apiService.patch('order/group', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: cleanObject(payload)
      });
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (!calculatedOrder?.id || firstLoad.current) return;
    updateDeliveryDetails();
    firstLoad.current = false;
  }, [calculatedOrderForm.scheduled, calculatedOrderForm.lat]);

  const subTotal =
    Number(calculatedOrder?.total_amount) -
    Number(calculatedOrder?.delivery_fee) -
    Number(calculatedOrder?.service_charge);
  const handleUpdateCalculatedOrderForm = (selectedAddress: LocationObjectType) => {
    if (selectedAddress) {
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        address_details: {
          name: selectedAddress.name,
          address_line: selectedAddress.address,
          city: selectedAddress.address.split(',')[1] ?? '',
          phone_number: DEFAULT_PHONE_NUMBER ?? '',
          building_number: selectedAddress.street_number ?? ''
        },
        lat: selectedAddress.lat.toString(),
        lng: selectedAddress.lng.toString()
      });
    }
  };
  const editOrder = (meal: OrderMealType) => {
    navigate(`/orders/meals/${meal.id}?type=group`, { state: { created_at: meal.created_at } });
  };
  const lockOrder = async () => {
    const { id } = calculatedOrder;
    try {
      const payload = {
        group_locked: true,
        group_host_ready: true,
        calculated_order_id: id
      };
      await apiService.patch('order/group', {
        baseURL: process.env.REACT_APP_ORDER_API_URL,
        data: cleanObject(payload)
      });
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <Fragment>
      {
        <div className='w-full'>
          <div className='bg-[#F7F7F8] w-full h-[85vh] lg:h-[85vh] rounded-xl p-1 flex flex-col-reverse lg:flex-row gap-2 lg:gap-6 lg:justify-between basket'>
            <div className='w-full lg:w-[48.5%] h-full relative'>
              <div className='w-full h-[30vh] pb-12 mt-[-2rem] lg:mt-0 lg:pb-0 lg:h-[80%] overflow-y-scroll no-scrollbar'>
                <div className='w-full bg-white pt-7 px-6 pb-4 rounded-tl-lg mb-3'>
                  <h4 className='mb-3 font-semibold text-[#2E3031] text-2xl'>Delivery details</h4>
                  <div className='w-full flex justify-between items-center pb-3 border-b-[0.7px] border-b-[#EAEBEB]'>
                    <div className='flex gap-2 text-gray-9 items-center'>
                      <CalendarAltIcon />
                      <div>
                        <p className='text-gray-10'>
                          {calculatedOrderForm?.scheduled_delivery_date !== '' &&
                          calculatedOrderForm?.scheduled_delivery_time !== ''
                            ? moment(calculatedOrderForm?.scheduled_delivery_date).format(
                                'ddd, Do MMM'
                              ) +
                              ', ' +
                              formatDeliveryTime(calculatedOrderForm?.scheduled_delivery_time)
                            : 'Schedule a delivery'}
                        </p>
                        {calculatedOrderForm.recurring && (
                          <p className='text-[#6F7376] text-xs'>
                            Repeats weekly on{' '}
                            <span className='text-[#0080E5]'>
                              {formatDaysOfWeek(calculatedOrderForm.selected_days)}
                            </span>{' '}
                            until {''}
                            {moment(calculatedOrderForm.recurring_order_end_date).format(
                              'Do MMM YYYY'
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className='w-7 h-7 rounded-full bg-[#F7F7F8] flex justify-center items-center text-gray-6'>
                      <ArrowRightIcon
                        className='cursor-pointer'
                        onClick={() => setShowScheduledDeliveryModal(true)}
                      />
                    </div>
                  </div>
                  <div className='w-full flex justify-between pb-3 border-b-[0.7px] border-b-[#EAEBEB] items-center mt-4 text-gray-6'>
                    <div className='w-[85%] flex gap-2 text-gray-9 items-center'>
                      <LocationAltIcon />
                      <div className='w-[95%]'>
                        <p className='text-gray-6 line-clamp-1'>
                          {calculatedOrderForm?.address_details?.address_line}
                        </p>
                      </div>
                    </div>
                    <RmxEditIcon
                      onClick={() => setShowAddressModal(true)}
                      className='cursor-pointer'
                    />
                  </div>
                  <div className='w-full flex justify-between items-center mt-4 text-gray-6'>
                    <div className='flex gap-2 text-gray-9 items-center'>
                      <PhoneIcon />
                      <p className='text-gray-6'>
                        {calculatedOrder?.address_details?.phone_number || DEFAULT_PHONE_NUMBER}
                      </p>
                    </div>
                    <RmxEditIcon
                      className='cursor-pointer'
                      onClick={() => setShowPhoneNumberModal(true)}
                    />
                  </div>
                </div>
                <div className='w-full py-5 px-6 flex justify-between items-center bg-white gap-4 mb-3'>
                  <div className='flex gap-2'>
                    <CutleryIcon />
                    {createOrderForm?.order_details?.cutlery ? (
                      <div>
                        <p className='text-[#2E3031] text-sm'>Cutlery included</p>
                        <p className='text-[#6F7376] text-xs'>
                          Help us <span className='text-[#3FB827]'>reduce plastic waste</span> by
                          removing cutlery if not needed
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className='text-[#2E3031] text-sm'>No cutlery included</p>
                        <p className='text-[#6F7376] text-sm'>
                          Tap to add cutlery if you need it (free)
                        </p>
                      </div>
                    )}
                  </div>
                  <Input
                    type='checkbox'
                    inputClass='w-4 h-4'
                    checked={createOrderForm?.order_details?.cutlery}
                    readOnly
                    onChange={(e) =>
                      setCreateOrderForm({
                        ...createOrderForm,
                        order_details: {
                          ...createOrderForm.order_details,
                          cutlery: e.target.checked
                        }
                      })
                    }
                  />
                </div>
                <div className='w-full py-5 px-6 bg-white mb-3 '>
                  <div className='w-full mt-4 flex justify-between'>
                    <p className='text-[#6F7376] text-sm'>
                      {createOrderForm.order_details.appreciation_note !== ''
                        ? truncateString(createOrderForm.order_details.appreciation_note, 4)
                        : 'Appreciation note'}
                    </p>
                    <p
                      className='text-red-5 text-sm font-medium cursor-pointer'
                      onClick={() => setShowAppreciationNoteModal(true)}>
                      {createOrderForm.order_details.appreciation_note === '' ? 'Add' : 'Edit'}
                    </p>
                  </div>
                </div>
                {calculatedOrder?.sub_calculated_order_ids?.every(
                  (user) => user.group_guests_completed === false
                ) ? (
                  <div className='bg-[#FFF4E5] py-2 px-6 text-sm text-[#FF9500]'>
                    <p className='flex gap-2 items-center'>
                      {' '}
                      <InfoIcon /> All participants should complete orders to proceed
                    </p>
                  </div>
                ) : calculatedOrderForm.scheduled_delivery_time === '' ? (
                  <div className='bg-[#FFF4E5] py-2 px-6 text-sm text-[#FF9500]'>
                    <p className='flex gap-2 items-center'>
                      {' '}
                      <InfoIcon /> Select delivery time to proceed
                    </p>
                  </div>
                ) : null}
                <div className='w-full py-4 px-6 bg-white mb-3'>
                  <div className='flex justify-between mb-3'>
                    <p className='text-[#6F7376] text-sm'>Subtotal</p>
                    <p className='text-[#6F7376] text-sm font-medium'>{formatCurrency(subTotal)}</p>
                  </div>
                  <div className='flex justify-between mb-3'>
                    <p className='text-[#6F7376] text-sm'>Delivery fee</p>
                    <p className='text-[#6F7376] text-sm font-medium'>
                      {formatCurrency(calculatedOrder?.delivery_fee)}
                    </p>
                  </div>
                  <div className='flex justify-between mb-3'>
                    <div className='flex gap-1 items-center'>
                      <p className='text-[#6F7376] text-sm'>Service fee</p>
                      <QuestionIcon className='question-icon' />
                    </div>
                    <p className='text-[#6F7376] text-sm font-medium'>
                      {formatCurrency(calculatedOrder?.service_charge)}
                    </p>
                  </div>
                </div>
              </div>
              <div className='w-full rounded-bl-lg bg-white px-6 pt-6 pb-8 absolute bottom-0 left-0 total-box'>
                <div className='flex justify-between mb-3'>
                  <p className='text-[#6F7376] text-sm'>Order total</p>
                  <p className='text-[#2E3031] text-sm font-medium'>
                    {formatCurrency(calculatedOrder?.total_amount)}
                  </p>
                </div>
                {calculatedOrder?.group_locked ? (
                  <Button
                    variant='block'
                    color='primary'
                    className='w-full h-[53px] !max-w-full'
                    onClick={confirm}
                    disabled={isDisabled}>
                    Confirm Order
                  </Button>
                ) : (
                  <Button
                    variant='block'
                    color='primary'
                    className='w-full h-[53px] !max-w-full'
                    onClick={lockOrder}
                    disabled={calculatedOrder?.sub_calculated_order_ids?.every(
                      (user) => !user.group_guests_completed
                    )}>
                    Lock Order
                  </Button>
                )}
              </div>
            </div>
            <div className='w-full lg:w-[48.5%] h-full overflow-y-scroll no-scrollbar'>
              <div className='w-full pt-7 px-6 pb-2 bg-white rounded-tr-lg'>
                <div className='w-full flex justify-between'>
                  <h4 className='mb-3 font-semibold text-[#2E3031] text-xl'>Order summary</h4>
                  <div className='cursor-pointer' onClick={() => setShowCancelOrderModal(true)}>
                    <p className='text-red-5 text-sm'>Order info</p>
                  </div>
                </div>
              </div>
              {showQuickeatsConfirm && !ignoreQuickeats && (
                <QuickeatsBanner
                  onIgnore={() => setIgnoreQuickeats(true)}
                  onRemove={removeNonQuickeats}
                />
              )}
              <div className='w-full bg-white py-[10px] min-h-full'>
                {calculatedOrder?.meals?.map((restaurantMeal, index) => (
                  <div
                    className='flex flex-col border-b-[6px] border-[#F7F7F7] last:border-b-0'
                    key={index}>
                    <div className='flex items-center gap-2 py-4 px-7'>
                      <h4 className='font-medium'>{restaurantMeal.brand.name}</h4>
                    </div>
                    <ul>
                      {restaurantMeal?.meals?.map((meal, index) => (
                        <MealItem
                          price={meal.amount}
                          onClick={() => editOrder(meal)}
                          type='group'
                          key={index}
                          meal={meal}
                          removeMeal={() =>
                            BulkOrderService.RemoveMealFromOrder(
                              meal,
                              groupOrderMeals,
                              setGroupOrderMeals
                            )
                          }
                        />
                      ))}
                    </ul>
                  </div>
                ))}

                {calculatedOrder?.sub_calculated_order_ids?.length === 0 ? (
                  <div className='flex items-center justify-center gap-2 py-12 '>
                    <div className='min-w-full px-6'>
                      <h4 className='font-medium mb-9 text-[#6F7376] text-center'>
                        Waiting for others to join...
                      </h4>
                      <Button
                        variant='block'
                        color='primary'
                        small
                        className='mx-auto'
                        onClick={() => setShowMembersModal(true)}>
                        Invite others to join
                      </Button>
                    </div>
                  </div>
                ) : (
                  calculatedOrder?.sub_calculated_order_ids?.map((restaurantMeal, index) => (
                    <div
                      className='flex flex-col border-b-[6px] border-[#F7F7F7] last:border-b-0'
                      key={index}>
                      <div className='bg-[#F7F7F8] py-3 flex gap-4 justify-between items-center'>
                        <div className='flex gap-4 items-center'>
                          <div className='ml-7 p-3 bg-white rounded-full '>
                            <UserIcon />
                          </div>
                          <div>
                            <p className='font-medium text-sm'>{restaurantMeal.user.first_name}</p>
                            <p className='text-xs text-[#6F7376]'>
                              {restaurantMeal?.brand_ids?.length || 0}{' '}
                              {restaurantMeal?.brand_ids?.length === 1 ? 'brand' : 'brands'},&nbsp;
                              {Array.isArray(restaurantMeal?.meals)
                                ? getItemsTotal(restaurantMeal?.meals)
                                : 0}
                              {Array.isArray(restaurantMeal?.meals) &&
                              getItemsTotal(restaurantMeal?.meals) === 1
                                ? ' item'
                                : ' items'}
                            </p>
                          </div>
                        </div>
                        <div className=' p-2 bg-white rounded-full cursor-pointer mr-2 w-fit '>
                          {showMeals.includes(restaurantMeal?.user.id) ? (
                            <ArrowDownIcon
                              className='rotate-180 '
                              onClick={() =>
                                setShowMeals(
                                  showMeals.filter((id) => restaurantMeal?.user.id !== id)
                                )
                              }
                            />
                          ) : (
                            <ArrowDownIcon
                              onClick={() => setShowMeals([...showMeals, restaurantMeal?.user.id])}
                            />
                          )}
                        </div>
                      </div>
                      <div>
                        {showMeals.includes(restaurantMeal.user.id) && (
                          <>
                            {/* <div className='flex items-center gap-2 py-4 px-7'>
                            <h4 className='font-medium'>{restaurantMeal?.brand?.name}</h4>
                          </div> */}
                            <ul>
                              {Array.isArray(restaurantMeal?.meals) &&
                                restaurantMeal?.meals?.map((meal, index) =>
                                  meal?.meals?.map((nestedMeal) => (
                                    <MealItem
                                      type='group'
                                      key={index}
                                      meal={nestedMeal}
                                      price={meal?.amount}
                                    />
                                  ))
                                )}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <ScheduledDeliveryModal
            handleClick={(k) => {
              setcalculatedOrderForm({ ...calculatedOrderForm, ...k });
              setShowScheduledDeliveryModal(false);
            }}
            isOpen={showScheduledDeliveryModal}
            onClose={() => setShowScheduledDeliveryModal(false)}
            type='group'
          />

          <AddressModal
            open={showAddressModal}
            onClose={() => setShowAddressModal(false)}
            onConfirm={(address) => {
              handleUpdateCalculatedOrderForm(address);
            }}
            defaultSelectedAddress={calculatedOrderForm.address_details}
          />
          <GroupOrderInfoModal
            show={showCancelOrderModal}
            onClose={() => setShowCancelOrderModal(false)}
            onConfirm={cancelOrder}
          />
          <DiscountCodeModal
            show={showDiscountCodeModal}
            onClose={() => setShowDiscountCodeModal(false)}
          />
          <AppreciationModal
            show={showAppreciationNoteModal}
            onClose={() => setShowAppreciationNoteModal(false)}
          />
          <AddNewNumberModal
            show={showPhoneNumberModal}
            onClose={() => setShowPhoneNumberModal(false)}
          />
          {showMembersModal && (
            <AssignMealModal
              title='Invite Members'
              show={showMembersModal}
              onClose={() => setShowMembersModal(false)}
              handleClick={(members: MemberListType[]) => {
                inviteMembers(members);
              }}
              defaultMembers={[]}
            />
          )}
        </div>
      }
    </Fragment>
  );
};

export default GroupBasket;
